import { all, call, put, takeLatest, delay, race, take, fork, cancel, select } from 'redux-saga/effects';
import {
  fetchInterviewRequest,
  fetchInterviewSuccess,
  fetchInterviewFailure,
  startInterviewRequest,
  startInterviewSuccess,
  startInterviewFailure,
  fetchQuestionsRequest,
  fetchQuestionsSuccess,
  fetchQuestionsFailure,
  pollCurrentQuestionRequest,
  pollCurrentQuestionSuccess,
  pollCurrentQuestionFailure,
  submitAnswerRequest,
  submitAnswerSuccess,
  submitAnswerFailure,
  completeInterviewRequest,
  completeInterviewSuccess,
  completeInterviewFailure,
  setUploadPending,
  clearUploadPending,
  setSessionExpired,
} from '@store/slices/interviewSlice';
import { showToast } from '@store/slices/uiSlice';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002';
const POLL_INTERVAL = 30000; // 30 seconds
const CHUNKED_UPLOAD_MIN_BYTES = 5 * 1024 * 1024; // 5 MB
const CHUNKED_UPLOAD_CHUNK_BYTES = 10 * 1024 * 1024; // 10 MB
const CHUNKED_UPLOAD_RETRIES = 3;

// ========================================
// API Helper Functions
// ========================================

const apiCall = async (endpoint, options = {}) => {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    const errorMessage = data.error || data.message || data.error?.message || 'API request failed';
    const err = new Error(errorMessage);
    err.status = response.status;
    throw err;
  }

  return data;
};

// ========================================
// Fetch Interview Summary
// GET /api/public/interviews/:token
// ========================================
function* fetchInterviewSaga(action) {
  try {
    const token = action.payload;
    const data = yield call(apiCall, `/api/public/interviews/${token}`);
    
    yield put(fetchInterviewSuccess(data.data));
    
    // 🔥 AUTO-FETCH: If interview is already started, fetch questions
    if (data.data.interview?.is_started) {
      console.log('🔄 Interview already started, auto-fetching questions');
      yield put(fetchQuestionsRequest(token));
    }
    
  } catch (error) {
    if (error.status === 410) {
      yield put(fetchInterviewFailure('Interview session has expired'));
      yield put(setSessionExpired(true));
      return;
    }
    const errorMessage = error.message || 'Failed to fetch interview details';
    yield put(fetchInterviewFailure(errorMessage));
    yield put(showToast({ message: errorMessage, type: 'error' }));
  }
}

// ========================================
// Start Interview
// POST /api/public/interviews/:token/start
// ========================================
function* startInterviewSaga(action) {
  try {
    const { 
      sessionToken, 
      candidateName, 
      candidateEmail, 
      mediaMode, 
      termsAccepted, 
      termsVersion, 
      recordingConsent 
    } = action.payload;
    
    const requestData = {
      candidate_name: candidateName,
      candidate_email: candidateEmail,
      terms_accepted: termsAccepted,
      recording_consent: recordingConsent,
      terms_version: termsVersion,
      media_mode: mediaMode,
    };
    
    const data = yield call(apiCall, `/api/public/interviews/${sessionToken}/start`, {
      method: 'POST',
      body: JSON.stringify(requestData),
    });

    // Normalize backend variants so reducers consistently receive { interview: ... }
    const rawPayload = data?.data || {};
    const normalizedInterview = rawPayload.interview || rawPayload.session || rawPayload;

    yield put(startInterviewSuccess({ interview: normalizedInterview }));
    
    // Automatically fetch questions after starting
    yield put(fetchQuestionsRequest(sessionToken));
    
    const formatName = mediaMode === 'video' ? 'video' : mediaMode === 'audio' ? 'audio' : 'text';
    yield put(showToast({ 
      message: `Interview started with ${formatName} format!`, 
      type: 'success' 
    }));
    
  } catch (error) {
    const errorMessage = error.message || 'Failed to start interview';
    yield put(startInterviewFailure(errorMessage));
    yield put(showToast({ message: errorMessage, type: 'error' }));
  }
}

// ========================================
// Fetch Questions
// GET /api/public/interviews/:token/questions
// ========================================
function* fetchQuestionsSaga(action) {
  try {
    const token = action.payload;
    const data = yield call(apiCall, `/api/public/interviews/${token}/questions`);
    
    yield put(fetchQuestionsSuccess(data.data));
    
  } catch (error) {
    if (error.status === 410) {
      yield put(setSessionExpired(true));
      return;
    }
    const errorMessage = error.message || 'Failed to fetch questions';
    yield put(fetchQuestionsFailure(errorMessage));
    yield put(showToast({ message: errorMessage, type: 'error' }));
  }
}

// ========================================
// Poll Current Question (Lightweight)
// GET /api/public/interviews/:token/questions/current
// ========================================
function* pollCurrentQuestionSaga(action) {
  try {
    const token = action.payload;
    const data = yield call(apiCall, `/api/public/interviews/${token}/questions/current`);
    
    yield put(pollCurrentQuestionSuccess(data.data));
    
    // Check if session expired
    if (data.data.session_expired) {
      yield put(showToast({ 
        message: 'Interview session has expired', 
        type: 'warning' 
      }));
      
      // Stop polling
      return { stop: true };
    }
    
  } catch (error) {
    // Silent failure for polling - don't show error toast
    console.warn('Polling failed:', error.message);
    yield put(pollCurrentQuestionFailure(error.message));
  }
}

// ========================================
// Polling Loop Worker
// Polls every 30 seconds while interview is active
// ========================================
function* pollingSaga(token) {
  while (true) {
    try {
      // Wait 30 seconds
      yield delay(POLL_INTERVAL);
      
      // Poll current question
      const result = yield call(pollCurrentQuestionSaga, { payload: token });
      
      // Stop polling if session expired
      if (result?.stop) {
        break;
      }
      
    } catch (error) {
      console.warn('Polling loop error:', error);
      // Continue polling even if one poll fails
    }
  }
}

// ========================================
// Chunked Upload Helpers (direct-to-storage)
// ========================================
const readBlobAsArrayBuffer = (blobSlice) => blobSlice.arrayBuffer();

const putChunkToPresignedUrl = async (url, chunk) => {
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/octet-stream',
    },
    body: chunk,
  });

  if (!response.ok) {
    throw new Error(`Chunk upload failed with status ${response.status}`);
  }

  const etagHeader = response.headers.get('ETag') || response.headers.get('etag');
  if (!etagHeader) {
    throw new Error('Chunk upload succeeded but ETag header was missing');
  }

  return etagHeader;
};

function* uploadChunkedMediaSaga({
  sessionToken,
  responseId,
  blob,
  fileName,
  mimeType,
  mediaKind,
}) {
  let uploadId;
  let key;

  try {
    const startResponse = yield call(
      apiCall,
      `/api/public/interviews/${sessionToken}/answers/${responseId}/upload/start`,
      {
        method: 'POST',
        body: JSON.stringify({
          file_type: mimeType,
          file_name: fileName,
          media_kind: mediaKind,
        }),
      }
    );

    uploadId = startResponse?.data?.upload_id;
    key = startResponse?.data?.key;

    if (!uploadId || !key) {
      throw new Error('Invalid upload start response');
    }

    const parts = [];
    const totalChunks = Math.ceil(blob.size / CHUNKED_UPLOAD_CHUNK_BYTES);

    for (let index = 0; index < totalChunks; index += 1) {
      const partNumber = index + 1;
      const start = index * CHUNKED_UPLOAD_CHUNK_BYTES;
      const end = Math.min(start + CHUNKED_UPLOAD_CHUNK_BYTES, blob.size);
      const chunkSlice = blob.slice(start, end);
      const chunkBuffer = yield call(readBlobAsArrayBuffer, chunkSlice);

      const partResponse = yield call(
        apiCall,
        `/api/public/interviews/${sessionToken}/answers/${responseId}/upload/part?partNumber=${partNumber}&uploadId=${encodeURIComponent(uploadId)}&key=${encodeURIComponent(key)}`
      );

      const presignedUrl = partResponse?.data?.url;
      if (!presignedUrl) {
        throw new Error(`Missing presigned URL for part ${partNumber}`);
      }

      let lastError;
      let etag;

      for (let attempt = 1; attempt <= CHUNKED_UPLOAD_RETRIES; attempt += 1) {
        try {
          const rawEtag = yield call(putChunkToPresignedUrl, presignedUrl, chunkBuffer);
          etag = rawEtag.replace(/\"/g, '');
          break;
        } catch (error) {
          lastError = error;
          if (attempt < CHUNKED_UPLOAD_RETRIES) {
            yield delay(700 * attempt);
          }
        }
      }

      if (!etag) {
        throw lastError || new Error(`Failed to upload part ${partNumber}`);
      }

      parts.push({
        part_number: partNumber,
        etag,
      });
    }

    yield call(
      apiCall,
      `/api/public/interviews/${sessionToken}/answers/${responseId}/upload/complete`,
      {
        method: 'POST',
        body: JSON.stringify({
          upload_id: uploadId,
          key,
          parts,
        }),
      }
    );

    yield put(clearUploadPending());
    yield put(showToast({ message: 'Video upload completed', type: 'success' }));
  } catch (error) {
    if (uploadId && key) {
      try {
        yield call(
          apiCall,
          `/api/public/interviews/${sessionToken}/answers/${responseId}/upload/abort`,
          {
            method: 'POST',
            body: JSON.stringify({
              upload_id: uploadId,
              key,
            }),
          }
        );
      } catch (abortError) {
        console.warn('Chunked upload abort failed:', abortError.message);
      }
    }

    yield put(clearUploadPending());
    yield put(showToast({
      message: 'Upload failed. Please try re-recording or submit again.',
      type: 'warning',
    }));
    console.warn('Chunked upload failed:', error.message);
  }
}

// ========================================
// Start Polling (triggered after fetchQuestions or startInterview)
// ========================================
function* startPollingWatcher() {
  let pollingTask;
  let currentToken;
  
  while (true) {
    // Wait for fetchQuestionsSuccess or startInterviewSuccess
    const action = yield take([
      fetchQuestionsSuccess.type,
      startInterviewSuccess.type
    ]);
    
    // Cancel existing polling task if any
    if (pollingTask) {
      yield cancel(pollingTask);
    }
    
    // Get token from Redux state after action
    const state = yield select();
    currentToken = state.interview.sessionId || state.interview.interviewId;
    
    console.log('🔄 Starting polling with token:', currentToken);
    
    if (currentToken) {
      // Start new polling task
      pollingTask = yield fork(pollingSaga, currentToken);
    } else {
      console.warn('⚠️ No token found for polling');
    }
    
    // Stop polling when interview completes or expires
    yield race({
      complete: take(completeInterviewSuccess.type),
      expired: take((action) => 
        action.type === pollCurrentQuestionSuccess.type && 
        action.payload.session_expired
      ),
    });
    
    // Cancel polling
    if (pollingTask) {
      console.log('🛑 Stopping polling');
      yield cancel(pollingTask);
      pollingTask = null;
    }
  }
}

// ========================================
// Submit Answer
// POST /api/public/interviews/:token/answers
// ========================================
function* submitAnswerSaga(action) {
  try {
    const { 
      sessionToken, 
      questionId, 
      answerText, 
      durationSeconds, 
      answerType,
      formData, // For multipart with inline media
      chunkedUpload,
    } = action.payload;
    
    let data;
    
    if (chunkedUpload?.enabled && chunkedUpload?.blob?.size >= CHUNKED_UPLOAD_MIN_BYTES) {
      const requestData = {
        question_id: questionId,
        answer_text: answerText,
        duration_seconds: durationSeconds,
        answer_type: answerType || 'video',
      };

      data = yield call(apiCall, `/api/public/interviews/${sessionToken}/answers`, {
        method: 'POST',
        body: JSON.stringify(requestData),
      });

      yield put(submitAnswerSuccess(data.data));
      yield put(showToast({ message: 'Answer submitted successfully!', type: 'success' }));

      if (data.data?.answer?.id) {
        const responseId = data.data.answer.id;
        yield put(setUploadPending(responseId));
        yield fork(uploadChunkedMediaSaga, {
          sessionToken,
          responseId,
          blob: chunkedUpload.blob,
          fileName: chunkedUpload.fileName || `${chunkedUpload.mediaKind || 'video'}-answer.webm`,
          mimeType: chunkedUpload.mimeType || 'video/webm',
          mediaKind: chunkedUpload.mediaKind || 'video',
        });
      }

      return;
    }

    if (formData) {
      // Multipart submission with inline media
      const response = yield call(fetch, `${API_BASE_URL}/api/public/interviews/${sessionToken}/answers`, {
        method: 'POST',
        body: formData, // Don't set Content-Type - browser sets boundary
      });
      
      data = yield call([response, 'json']);
      
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to submit answer');
      }
    } else {
      // JSON submission
      const requestData = {
        question_id: questionId,
        answer_text: answerText,
        duration_seconds: durationSeconds,
        answer_type: answerType || 'text',
      };
      
      data = yield call(apiCall, `/api/public/interviews/${sessionToken}/answers`, {
        method: 'POST',
        body: JSON.stringify(requestData),
      });
    }
    
    console.log('📥 Answer submission response:', data.data);
    
    yield put(submitAnswerSuccess(data.data));

    // Background upload polling if the media file is being uploaded async
    if (data.data?.upload_pending && data.data?.answer?.id) {
      const responseId = data.data.answer.id;
      yield put(setUploadPending(responseId));
      yield fork(pollUploadStatusSaga, { sessionToken, responseId });
    }
    
    // 🔥 REMOVED: Don't auto-trigger complete - let reducer/UI handle it based on is_completed flag
    // The response already includes next_question, progress, and completion status
    
    yield put(showToast({ message: 'Answer submitted successfully!', type: 'success' }));
    
  } catch (error) {
    const errorMessage = error.message || 'Failed to submit answer';
    yield put(submitAnswerFailure(errorMessage));
    yield put(showToast({ message: errorMessage, type: 'error' }));
  }
}

// ========================================
// Poll Upload Status (background, non-blocking)
// GET /api/public/interviews/:token/answers/:responseId/upload-status
// ========================================
function* pollUploadStatusSaga({ sessionToken, responseId }) {
  const UPLOAD_POLL_INTERVAL = 2500; // 2.5 seconds
  const UPLOAD_TIMEOUT = 60000;     // 60 second max
  const startedAt = Date.now();

  while (Date.now() - startedAt < UPLOAD_TIMEOUT) {
    yield delay(UPLOAD_POLL_INTERVAL);
    try {
      const data = yield call(
        apiCall,
        `/api/public/interviews/${sessionToken}/answers/${responseId}/upload-status`
      );
      const status = data?.data || {};

      if (status.video_ready || status.audio_ready) {
        yield put(clearUploadPending());
        console.log('✅ Upload complete for response', responseId);
        return;
      }
    } catch (error) {
      // Silent — upload polling failures should not surface to the user
      console.warn('Upload status poll failed:', error.message);
    }
  }

  // Timeout reached — clear the indicator so it doesn't block the UI forever
  yield put(clearUploadPending());
  console.warn('⚠️ Upload polling timed out for response', responseId);
}

// ========================================
// Complete Interview
// POST /api/public/interviews/:token/complete
// ========================================
function* completeInterviewSaga(action) {
  try {
    const { sessionToken, completionData = {} } = action.payload;
    
    const data = yield call(apiCall, `/api/public/interviews/${sessionToken}/complete`, {
      method: 'POST',
      body: JSON.stringify(completionData),
    });
    
    yield put(completeInterviewSuccess(data.data));
    yield put(showToast({ message: 'Interview completed successfully!', type: 'success' }));
    
  } catch (error) {
    const errorMessage = error.message || 'Failed to complete interview';
    yield put(completeInterviewFailure(errorMessage));
    yield put(showToast({ message: errorMessage, type: 'error' }));
  }
}

// ========================================
// Root Saga
// ========================================
export default function* interviewSaga() {
  yield all([
    takeLatest(fetchInterviewRequest.type, fetchInterviewSaga),
    takeLatest(startInterviewRequest.type, startInterviewSaga),
    takeLatest(fetchQuestionsRequest.type, fetchQuestionsSaga),
    takeLatest(pollCurrentQuestionRequest.type, pollCurrentQuestionSaga),
    takeLatest(submitAnswerRequest.type, submitAnswerSaga),
    takeLatest(completeInterviewRequest.type, completeInterviewSaga),
    fork(startPollingWatcher), // Start polling watcher
  ]);
}